import React from "react";
import { StyleSheet } from "react-native";
import { colors, layout, shadows } from "@/constants";
import Interactive from "./Interactive";
import Icon, { IconName, IconSize } from "./Icon";

/**
 * Props interface for the ControlButton component
 */
interface ControlButtonProps {
  /** The icon name to display */
  icon: IconName;
  /** Size of the button in pixels (width and height) - defaults to 56 */
  size?: number;
  /** Whether the button is in active/enabled state */
  isActive?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Accessibility label for screen readers */
  accessibilityLabel?: string;
  /** Callback function triggered when the button is pressed */
  onPress: () => void;
}

/**
 * A filled, elevated, circular icon control — used for prominent transport-style
 * actions (mic, speaker, connect). For low-emphasis borderless icon actions use
 * {@link IconButton} instead. Built on the Interactive primitive.
 *
 * @param props - The component props
 * @returns A standardized filled circular control with an icon
 */
export default function ControlButton({
  icon,
  size = 56,
  isActive = true,
  disabled = false,
  accessibilityLabel,
  onPress
}: ControlButtonProps) {
  const backgroundColor = isActive
    ? colors.primaryContainer
    : colors.surfaceVariant;

  let iconSize: IconSize = "large";
  if (size < 40) iconSize = "medium";
  else if (size >= 56) iconSize = "xlarge";

  return (
    <Interactive
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.button,
        { width: size, height: size, borderRadius: size / 2, backgroundColor }
      ]}
    >
      <Icon
        name={icon}
        size={iconSize}
        style={{ color: colors.onPrimaryContainer }}
      />
    </Interactive>
  );
}

const styles = StyleSheet.create({
  button: {
    justifyContent: "center",
    alignItems: "center",
    padding: layout.space.xs,
    ...shadows.raised
  }
});
